import { Variable as ScriptVariable, FieldInfo, variablesToFieldsInfo } from '@microservices/wiskey-react-components'
import type { Variable } from '@mobileTypes'

export const COMMON_SCRIPT_VARIABLES: ScriptVariable[] = [
  {
    name: 'userData',
    type: [
      {
        name: 'tubNumber',
        type: 'string',
        description: 'Internal TUB/staff number of the user',
      },
      {
        name: 'email',
        type: 'string',
        description: 'User email address',
      },
      {
        name: 'name',
        type: 'string',
        description: 'Full name of the user',
      },
      {
        name: 'given_name',
        type: 'string',
        description: 'First name',
      },
      {
        name: 'family_name',
        type: 'string',
        description: 'Last name',
      },
      {
        name: 'preferred_username',
        type: 'string',
        description: 'Preferred username / login',
      },
      {
        name: 'company',
        type: 'string',
        description: 'Company/organization of the user',
      },
      {
        name: 'staffId',
        type: 'string',
        description: 'Staff identifier in external systems',
      },
      {
        name: 'realm_access.roles',
        type: 'string[]',
        description: 'Array of realm roles assigned to the user',
      },
      {
        name: 'resource_access',
        type: 'any',
        description: 'Roles per client (Keycloak resource_access object)',
      },
      {
        name: 'realm_access',
        type: 'any',
        description: 'RealmAccess object with roles[]',
      },
      {
        name: 'scope',
        type: 'string',
        description: 'OAuth scopes string',
      },
      {
        name: 'email_verified',
        type: 'boolean',
        description: 'Whether the user email is verified',
      },
    ],
    description: 'Current user JWT payload (common fields and roles)',
  },
  {
    name: 'route',
    type: 'any',
    description: 'Current route information',
  },
  {
    name: 'moment',
    type: 'typeof moment',
    description: 'Moment.js library helper',
  },
]

/** Command code for paginated read (object_read_all). Used to show previousResult hint only when dependsOn points to this command. */
export const OBJECT_READ_ALL_COMMAND_NAME = 'object_read_all'

/** Variable hint for ScriptValueDialog (EventFlow only): previousResult when dependsOn is execute_command object_read_all. */
export const PREVIOUS_RESULT_OBJECT_READ_ALL_SCRIPT_VARIABLE: ScriptVariable = {
  name: 'previousResult',
  type: [
    { name: 'data', type: 'any[]', description: 'Array of items' },
    { name: 'totalPages', type: 'number', description: 'Total pages count' },
    { name: 'totalElements', type: 'number', description: 'Total elements count' },
    { name: 'currentPage', type: 'number', description: 'Current page index' },
    { name: 'pageSize', type: 'number', description: 'Page size' },
  ],
  description: 'Previous command result: { data, totalPages, totalElements, currentPage, pageSize } (when dependsOn is object_read_all)',
}

/** FieldInfo for common script variables (derived via variablesToFieldsInfo; includes nested userData etc.). */
export const COMMON_SCRIPT_FIELDS_INFO: FieldInfo[] = variablesToFieldsInfo(COMMON_SCRIPT_VARIABLES)

/** Build variables list for form script dialogs (formData, item, localVariables, globalVariables, common). Omits localVariables/globalVariables when empty. */
export function buildFormScriptVariables(
  variables: Variable[],
  globalVariables: Variable[]
): ScriptVariable[] {
  const base: ScriptVariable[] = [
    { name: 'formData', type: 'object', description: 'Current form data' },
    { name: 'item', type: 'object', description: 'Item passed from navigation (if any)' },
  ]
  if (variables.length > 0) {
    base.push({
      name: 'localVariables',
      type: variables.map(v => ({
        name: v.internalId,
        type: v.valueType || 'any',
        description: `Form variable: ${v.internalId}`,
      })),
      description: 'Form variables',
    })
  }
  if (globalVariables.length > 0) {
    base.push({
      name: 'globalVariables',
      type: globalVariables.map(v => ({
        name: v.internalId,
        type: v.valueType || 'any',
        description: `Global variable: ${v.internalId}`,
      })),
      description: 'Global variables',
    })
  }
  return [...base, ...COMMON_SCRIPT_VARIABLES]
}

/** Build fieldsInfo for form script dialogs (formData, item, localVariables, globalVariables, common). Uses variablesToFieldsInfo for nested structure. */
export function buildFormScriptFieldsInfo(
  variables: Variable[],
  globalVariables: Variable[]
): FieldInfo[] {
  return variablesToFieldsInfo(buildFormScriptVariables(variables, globalVariables))
}

/** Build variables list for list item script dialogs (e.g. Get Data Commands JS params): localVariables, globalVariables, common. No previousValue (not in EventFlow). Omits localVariables/globalVariables when empty. */
export function buildListItemScriptVariables(
  variables: Variable[],
  globalVariables: Variable[]
): ScriptVariable[] {
  const base: ScriptVariable[] = []
  if (variables.length > 0) {
    base.push({
      name: 'localVariables',
      type: variables.map(v => ({
        name: v.internalId,
        type: v.valueType || 'any',
        description: `List item variable: ${v.internalId}`,
      })),
      description: 'List item variables',
    })
  }
  if (globalVariables.length > 0) {
    base.push({
      name: 'globalVariables',
      type: globalVariables.map(v => ({
        name: v.internalId,
        type: v.valueType || 'any',
        description: `Global variable: ${v.internalId}`,
      })),
      description: 'Global variables',
    })
  }
  return [...base, ...COMMON_SCRIPT_VARIABLES]
}

/** Build fieldsInfo for list item script dialogs (localVariables, globalVariables, common). Uses variablesToFieldsInfo for nested structure. */
export function buildListItemScriptFieldsInfo(
  variables: Variable[],
  globalVariables: Variable[]
): FieldInfo[] {
  return variablesToFieldsInfo(buildListItemScriptVariables(variables, globalVariables))
}
